import { useShallow } from 'zustand/react/shallow';
import { useStore } from '@/store/useStore';
import {
  STUCKED_TIMEOUT,
  IDLE_TIMEOUT,
} from '@/utilities/constants/clientTimeouts';
import { useRef, useEffect } from 'react';

/*
┌─────────────────────────────────────────────────────────────┐
│  use-client-timers (wrapper effects)                        │
│  - Watches gameLoadProgress + internalLoadProgress          │
│  - If truly stuck for 5-15s → isStuckedLoading = true       │
│  - Triggers error/reload UI                                 │
└─────────────────────────────────────────────────────────────
*/
const useClientTimers = (
  setIsStuckedLoading: (isStuckedLoading: boolean) => void,
) => {
  // Access store
  const gameResetRevision = useStore((s) => s.gameResetRevision);
  const {
    gameLoadStarted,
    gameLoadSuccess,
    gameLoadProgress,
    internalLoadProgress,
  } = useStore(useShallow((s) => s.gameStatus));

  const authToken = useStore((s) => s.userSessionContext.authToken);
  const reloadRef = useRef(gameResetRevision);

  // Initialize refs
  const progressEmittedRef = useRef(false);
  const prevGameLoadStartedRef = useRef(gameLoadStarted);
  const internalLoadProgressRef = useRef(internalLoadProgress);
  const overallTimeoutStartedRef = useRef(false);
  const overallTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Effect 1: Keep internalLoadProgress ref in sync for use in timer callbacks
  useEffect(() => {
    internalLoadProgressRef.current = internalLoadProgress;
  }, [internalLoadProgress]);

  // Effect 2: When a soft reload occurs, reset the stuck loading state and remove timers
  useEffect(() => {
    if (reloadRef.current !== gameResetRevision) {
      reloadRef.current = gameResetRevision;
      setIsStuckedLoading(false);
      progressEmittedRef.current = false;
      overallTimeoutStartedRef.current = false;
      if (overallTimeoutRef.current) {
        clearTimeout(overallTimeoutRef.current);
        overallTimeoutRef.current = null;
      }
    }
  }, [gameResetRevision, setIsStuckedLoading, reloadRef]);

  // Effect 3: Reset stuck loading state when game load restarts (status reset)
  useEffect(() => {
    // Reset stuck loading when game load transitions from started to not started
    // This indicates a reload/reset has occurred
    if (prevGameLoadStartedRef.current && !gameLoadStarted) {
      setIsStuckedLoading(false);
      progressEmittedRef.current = false;
      overallTimeoutStartedRef.current = false;
      if (overallTimeoutRef.current) {
        clearTimeout(overallTimeoutRef.current);
        overallTimeoutRef.current = null;
      }
    }
    prevGameLoadStartedRef.current = gameLoadStarted;
  }, [gameLoadStarted, setIsStuckedLoading, reloadRef]);

  // Effect 4: Track when gameLoadProgress changes (emitter progress)
  useEffect(() => {
    if (gameLoadProgress > 0) {
      progressEmittedRef.current = true;
    }
  }, [gameLoadProgress, reloadRef]);

  // Effect 5: Game load started cases - depends on gameLoadProgress to reset timer when emitter sends updates
  useEffect(() => {
    let stuckTimer: ReturnType<typeof setTimeout> | null = null;

    // Reset stuck loading when game load succeeds
    if (gameLoadSuccess) {
      setIsStuckedLoading(false);
      return () => {
        if (stuckTimer) clearTimeout(stuckTimer);
      };
    }
    // Case 2: Game load started but not succeeded, and emitter progress has been received
    // but might have stalled - use 5 second delay
    // Timer resets every time gameLoadProgress changes (new emitter update)
    if (gameLoadStarted && !gameLoadSuccess && progressEmittedRef.current) {
      stuckTimer = setTimeout(() => {
        if (authToken && !gameLoadSuccess) {
          setIsStuckedLoading(true);
        }
      }, STUCKED_TIMEOUT * 1000);

      return () => {
        if (stuckTimer) clearTimeout(stuckTimer);
      };
    }

    // Case 3: Game load started but no emitter progress ever received
    // Use 15 second delay (IDLE_TIMEOUT)
    if (gameLoadStarted && !gameLoadSuccess && !progressEmittedRef.current) {
      stuckTimer = setTimeout(() => {
        if (authToken && !gameLoadSuccess) {
          setIsStuckedLoading(true);
        }
      }, IDLE_TIMEOUT * 1000);

      return () => {
        if (stuckTimer) clearTimeout(stuckTimer);
      };
    }

    return () => {
      if (stuckTimer) clearTimeout(stuckTimer);
    };
  }, [
    gameLoadStarted,
    gameLoadSuccess,
    gameLoadProgress,
    authToken,
    setIsStuckedLoading,
    reloadRef,
  ]);

  // Effect 6: Separate effect for internalLoadProgress threshold - handles game fetch failure case
  // This effect ONLY triggers when internalLoadProgress crosses the 90 threshold
  useEffect(() => {
    let stuckTimer: ReturnType<typeof setTimeout> | null = null;

    // Skip if game load succeeded or is in progress properly
    if (gameLoadSuccess || gameLoadStarted) {
      // Clear the overall timeout if game succeeded or started
      if (overallTimeoutRef.current) {
        clearTimeout(overallTimeoutRef.current);
        overallTimeoutRef.current = null;
      }
      overallTimeoutStartedRef.current = false;
      return () => {
        if (stuckTimer) clearTimeout(stuckTimer);
      };
    }

    // Case 1: Game fetch failed - gameLoadStarted is false, gameLoadSuccess is false,
    // but internalLoadProgress reached 90+ (loading bar nudged forward on its own)
    // Wait 5 seconds then set isStucked
    if (!gameLoadStarted && !gameLoadSuccess && internalLoadProgress >= 90) {
      stuckTimer = setTimeout(() => {
        if (authToken) {
          setIsStuckedLoading(true);
        }
      }, STUCKED_TIMEOUT * 1000);

      return () => {
        if (stuckTimer) clearTimeout(stuckTimer);
      };
    }

    // Case 4: Game hasn't started yet, no success, but internalLoadProgress is building up
    // Start a ONE-SHOT 15 second timer when we first detect this condition
    // This timer doesn't reset on subsequent progress changes - it's a fallback safety net
    if (
      !gameLoadStarted &&
      !gameLoadSuccess &&
      internalLoadProgress > 0 &&
      internalLoadProgress < 90
    ) {
      if (!overallTimeoutStartedRef.current) {
        overallTimeoutStartedRef.current = true;
        overallTimeoutRef.current = setTimeout(() => {
          // Re-check current state via refs/closure
          if (authToken && !gameLoadStarted && !gameLoadSuccess) {
            // Check if we're now at 90+ which will be handled by Case 1
            if (internalLoadProgressRef.current >= 90) {
              return;
            }
            setIsStuckedLoading(true);
          }
          overallTimeoutRef.current = null;
        }, IDLE_TIMEOUT * 1000);
      }
      // Don't return cleanup here - we want the one-shot timer to persist (effect 6)
    }

    return () => {
      if (stuckTimer) clearTimeout(stuckTimer);
    };
  }, [
    gameLoadStarted,
    gameLoadSuccess,
    internalLoadProgress,
    authToken,
    setIsStuckedLoading,
    reloadRef,
  ]);
};

export default useClientTimers;
